import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import { Grid, FormControl, FormHelperText, MenuItem, Box, InputLabel } from "@mui/material";
import Select from "@mui/material/Select";
import { useProgramme } from "../../contexts/programme.context";
import { Formik, FormikHelpers } from "formik";
import * as yup from "yup";
import { ProgrammeFrequency } from "../../entities/ProgrammeDetails";
import { Organisation, Tier } from "common/build/prisma/client";
import { Alert } from "@mui/material";
import { ProgrammeType } from "../../../../models/modelTypes";
import dayjs from "dayjs";
import { PrimaryButton } from "../../../layout/Button/Button";
import { FormActions } from "../../../../core/components/FormActions";
import { WizardHeading } from "../../../../core/components/WizardHeading";
import { ToggleInput } from "../../../../core/components/ToggleInput";
import { VideoConferenceSelect } from "../../../session/components/VideoConferenceSelect";
import { useAuth } from "../../../../contexts/auth.context";
import { useAppNavigate } from "../../../../hooks/useAppNavigate";
import { connectFacilitatorUserToProgramme } from "../../../../client/api/programme";
import { isSoloPersonalOrganisation } from "../../../../utils/courseDemo";
import { getRoleDisplayName } from "../../../users/entities/RoleType";

type ProgrammeDetailsProps = {
    organisation: (Organisation & { Tier?: Tier | null }) | undefined;
};

export type AddProgrammeType = ProgrammeType & {
    date: string;
    time: string;
};

const ProgrammeDetails: React.FC<ProgrammeDetailsProps> = (props: ProgrammeDetailsProps): JSX.Element => {
    const {
        changeStep,
        details,
        submitStep,
        addNewProgramme,
        updateProgramme,
        programme,
        addStoryCollectionToProgramme,
        generateProgrammeSessions,
        clearProvider,
    } = useProgramme();
    const { user } = useAuth();
    const { push } = useAppNavigate();

    const { name, isGroup, isOnline, startDate, frequency, providerType, providerURL } = details;

    const [error, setError] = useState<string>();

    const isSoloPersonalOrg = isSoloPersonalOrganisation(props.organisation ?? user?.Organisation, user?.isSchool);

    const completeSoloDemoProgramme = async (createdProgramme: ProgrammeType): Promise<void> => {
        const facilitatorLabel = getRoleDisplayName("Facilitator", user?.isSchool).toLowerCase();
        if (!user?.email) {
            throw `Unable to assign ${facilitatorLabel} for course demo account`;
        }
        const response = await connectFacilitatorUserToProgramme(createdProgramme.id, { email: user.email }, false);
        if (!response.ok) {
            const body = (await response.json().catch(() => undefined)) as { message?: string } | undefined;
            throw body?.message ?? `Unable to assign ${facilitatorLabel} for course demo account`;
        }
        submitStep(createdProgramme);
        await clearProvider();
        push(`/programmes/view/${createdProgramme.id}`);
    };

    const onSubmit = async (
        values: Partial<AddProgrammeType>,
        { setSubmitting }: FormikHelpers<Partial<AddProgrammeType>>,
    ) => {
        const { date, time, ...programmeDetails } = values;
        const newDate = dayjs(`${date} ${time}`, "YYYY-MM-DD HH:mm").toDate();
        const stepData = {
            ...programmeDetails,
            startDate: newDate,
            organisationId: props.organisation?.id,
            storyCollectionId: details.storyCollectionId,
            ageRange: values.ageRange || details?.ageRange,
        };

        try {
            submitStep(stepData);
            if (programme && programme.id) {
                const updatedProgramme = await updateProgramme(programme.id, stepData);
                setError(undefined);
                if (details?.storyCollectionId) {
                    await addStoryCollectionToProgramme(details.storyCollectionId, programme.id);
                    await generateProgrammeSessions(programme.id);
                }
                if (isSoloPersonalOrg) {
                    await completeSoloDemoProgramme(updatedProgramme);
                    return;
                }
                changeStep(2);
            } else {
                const createdProgramme = await addNewProgramme(stepData);
                setError(undefined);
                if (isSoloPersonalOrg) {
                    await completeSoloDemoProgramme(createdProgramme);
                    return;
                }
                changeStep(2);
            }
        } catch (submitError) {
            setError(typeof submitError === "string" ? submitError : "Unable to save programme details");
        } finally {
            setSubmitting(false);
        }
    };

    const ageRanges = [
        ...(() => {
            const ageRanges: string[] = [];
            for (let i = 4; i < 17; i++) {
                ageRanges.push(`${i}-${i + 1}`);
            }
            return ageRanges;
        })(),
        "18+",
    ];

    return (
        <form>
            <WizardHeading overline={"Schedule a programme"} heading={"Programme details"} />
            <Formik
                initialValues={{
                    name: name ?? "",
                    isGroup: isGroup ?? true,
                    ageRange: details?.ageRange ?? "",
                    isOnline: isOnline ?? false,
                    date: dayjs(startDate).format("YYYY-MM-DD") ?? dayjs(new Date()).format("YYYY-MM-DD"),
                    time: dayjs(startDate).format("HH:mm") ?? "08:30",
                    frequency: frequency ?? ProgrammeFrequency.Weekly,
                    providerType: providerType ?? "BuiltIn",
                    providerURL: providerURL ?? "",
                }}
                onSubmit={onSubmit}
                validationSchema={yup.object({
                    name: yup.string().required("Programme name is required"),
                    isGroup: yup.boolean().required("A group or 1-1 is required"),
                    ageRange: yup.string().required("Age Range is required"),
                    isOnline: yup.boolean().required("Online or in person is required"),
                    date: yup.string().required("A Start Date is required"),
                    time: yup.string().required("A Start Time is required"),
                    frequency: yup.string().required("Frequency is required"),
                    providerURL: isOnline
                        ? yup.string().when("providerType", {
                              is: "BuiltIn",
                              then: yup.string().notRequired(),
                              otherwise: yup
                                  .string()
                                  .matches(
                                      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9-_#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
                                      "Video URL must be a valid URL",
                                  )
                                  .required(),
                          })
                        : yup.string().notRequired(),
                })}
                validateOnChange={false}
                enableReinitialize
            >
                {({ submitForm, errors, values, handleChange, touched, isSubmitting, setFieldValue }) => {
                    return (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    autoFocus
                                    required
                                    name="name"
                                    variant="outlined"
                                    label="Programme Name"
                                    value={values.name}
                                    fullWidth
                                    onChange={handleChange}
                                    helperText={errors.name && touched.name && errors.name}
                                    error={!!errors.name}
                                />
                            </Grid>
                            <ToggleInput
                                id="isGroup"
                                setFieldValue={setFieldValue}
                                label="Programme format"
                                leftOption="Group"
                                rightOption="1-2-1"
                                value={values.isGroup ?? true}
                            />
                            <Grid item xs={12}>
                                <FormControl variant="outlined" error={!!errors.ageRange} fullWidth>
                                    <InputLabel id="age-label" required>
                                        Age Group of Participants
                                    </InputLabel>
                                    <Select
                                        variant="outlined"
                                        fullWidth
                                        disabled={details.ageRange ? true : false}
                                        label="Age Group of Participants"
                                        labelId="age-label"
                                        name="ageRange"
                                        id="ageRange"
                                        value={values.ageRange}
                                        onChange={(e) => setFieldValue("ageRange", e.target.value)}
                                    >
                                        <MenuItem key={0} value={""} disabled>
                                            Please select
                                        </MenuItem>
                                        {ageRanges.map((ageRange) => (
                                            <MenuItem key={ageRange} value={ageRange}>
                                                {ageRange}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {errors.ageRange && touched.ageRange && errors.ageRange}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container spacing={3}>
                                    <Grid item xs={6}>
                                        <TextField
                                            name="date"
                                            id="date"
                                            label="Start Date"
                                            type="date"
                                            variant="outlined"
                                            value={values.date}
                                            onChange={handleChange}
                                            helperText={errors.date && touched.date && errors.date}
                                            error={!!errors.date}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            name="time"
                                            id="time"
                                            label="Start Time"
                                            type="time"
                                            variant="outlined"
                                            inputProps={{
                                                step: 300,
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            value={values.time}
                                            onChange={handleChange}
                                            helperText={errors.time && touched.time && errors.time}
                                            error={!!errors.time}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl variant="outlined" error={!!errors.frequency} fullWidth>
                                            <InputLabel id="frequency-label" required>
                                                Frequency
                                            </InputLabel>
                                            <Select
                                                variant="outlined"
                                                labelId="frequency-label"
                                                name="frequency"
                                                id="frequency"
                                                value={values.frequency}
                                                label="Frequency"
                                                onChange={(e) => setFieldValue("frequency", e.target.value)}
                                                fullWidth
                                            >
                                                {Object.entries(ProgrammeFrequency).map(([value, label]) => {
                                                    return (
                                                        <MenuItem key={value} value={value}>
                                                            {label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                            <FormHelperText>
                                                {errors.frequency && touched.frequency && errors.frequency}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <ToggleInput
                                id={`isOnline`}
                                setFieldValue={setFieldValue}
                                label={`How / Where will the sessions be held?`}
                                leftOption={`Online`}
                                rightOption={`In-Person`}
                                value={values.isOnline ?? false}
                            />
                            <VideoConferenceSelect
                                setFieldValue={setFieldValue}
                                values={values as Partial<ProgrammeType>}
                                errors={errors as Partial<ProgrammeType>}
                            />
                            <Grid item xs={12} style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                                Input fields marked with an asterisk (*) are required
                            </Grid>
                            <Grid item xs={12}>
                                {error && (
                                    <Box mb={2}>
                                        <Alert severity="error">{error}</Alert>
                                    </Box>
                                )}
                                <FormActions
                                    right={
                                        <>
                                            <PrimaryButton
                                                size="large"
                                                disabled={isSubmitting}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    submitForm();
                                                }}
                                            >
                                                Continue
                                            </PrimaryButton>
                                        </>
                                    }
                                />
                            </Grid>
                        </Grid>
                    );
                }}
            </Formik>
        </form>
    );
};

export default ProgrammeDetails;
